import { useEffect, useMemo, useRef, useState } from "react";
import GridLayout, { Layout } from "react-grid-layout";

import { usePreferences } from "@/hooks/usePreferences";

import { ChartPane } from "./ChartPane";
import { WatchlistPane, type WatchSymbol } from "./WatchlistPane";
import { BookPane } from "./BookPane";
import { PositionsPane } from "./PositionsPane";
import { OrderTicketPane } from "./OrderTicketPane";
import { TapePane } from "./TapePane";
import { MarginPane } from "./MarginPane";
import { OpenOrdersPane } from "./OpenOrdersPane";
import { EquityCurvePane } from "./EquityCurvePane";

import "react-grid-layout/css/styles.css";
import "react-resizable/css/styles.css";

const STORAGE_KEY = "piggy.terminal.layout.v1";

const DEFAULT_LAYOUT: Layout[] = [
  { i: "chart", x: 0, y: 0, w: 8, h: 14, minW: 4, minH: 6 },
  { i: "watchlist", x: 8, y: 0, w: 4, h: 7, minW: 2, minH: 4 },
  { i: "book", x: 8, y: 7, w: 4, h: 7, minW: 2, minH: 4 },
  { i: "positions", x: 0, y: 14, w: 4, h: 5, minW: 3, minH: 3 },
  { i: "openorders", x: 4, y: 14, w: 3, h: 5, minW: 2, minH: 3 },
  { i: "margin", x: 7, y: 14, w: 2, h: 5, minW: 2, minH: 3 },
  { i: "tape", x: 9, y: 14, w: 1, h: 5, minW: 1, minH: 3 },
  { i: "ticket", x: 10, y: 14, w: 2, h: 5, minW: 2, minH: 3 },
  { i: "equity", x: 0, y: 19, w: 12, h: 5, minW: 4, minH: 3 },
];

function loadLayout(): Layout[] {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (raw) {
      const parsed = JSON.parse(raw) as Layout[];
      if (Array.isArray(parsed) && parsed.every((p) => typeof p.i === "string")) {
        return parsed;
      }
    }
  } catch {
    /* fall through */
  }
  return DEFAULT_LAYOUT;
}

export function TerminalPage() {
  const prefs = usePreferences();
  const [layout, setLayout] = useState<Layout[]>(() => loadLayout());
  const [symbol, setSymbol] = useState<WatchSymbol>({ symbol: "AAPL", asset_class: "EQUITY" });
  const [width, setWidth] = useState(0);
  const containerRef = useRef<HTMLDivElement | null>(null);

  // Pull saved layout from the user's prefs blob once it loads. The
  // localStorage fallback covers the brief window before the API
  // resolves and the offline case.
  useEffect(() => {
    const saved = (prefs.data as Record<string, unknown>).terminal_layout;
    if (Array.isArray(saved) && saved.every((p: unknown) => typeof (p as { i?: unknown }).i === "string")) {
      setLayout(saved as Layout[]);
    }
  }, [prefs.data]);

  // Restore last-selected symbol too.
  useEffect(() => {
    const saved = (prefs.data as Record<string, unknown>).terminal_symbol;
    if (saved && typeof saved === "object" && (saved as { symbol?: string }).symbol) {
      setSymbol(saved as WatchSymbol);
    }
  }, [prefs.data]);

  // Track container width for react-grid-layout (it doesn't auto-flex).
  useEffect(() => {
    if (!containerRef.current) return;
    const el = containerRef.current;
    const ro = new ResizeObserver(() => setWidth(el.clientWidth));
    ro.observe(el);
    setWidth(el.clientWidth);
    return () => ro.disconnect();
  }, []);

  function persist(next: Layout[]) {
    setLayout(next);
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(next));
    } catch {
      /* ignore quota / private mode */
    }
    prefs.patch({ terminal_layout: next });
  }

  function selectSymbol(s: WatchSymbol) {
    setSymbol(s);
    prefs.patch({ terminal_symbol: s });
  }

  const panels = useMemo(() => ({
    chart: <ChartPane symbol={symbol} />,
    watchlist: <WatchlistPane selected={symbol} onSelect={selectSymbol} />,
    book: <BookPane symbol={symbol} />,
    positions: <PositionsPane onSelect={(s) => selectSymbol({ symbol: s, asset_class: "EQUITY" })} />,
    openorders: <OpenOrdersPane />,
    margin: <MarginPane />,
    tape: <TapePane />,
    ticket: <OrderTicketPane symbol={symbol} />,
    equity: <EquityCurvePane />,
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }), [symbol]);

  // Mobile fallback — at <768px the drag-grid is unusable; render the
  // panes in a stacked single-column layout in priority order.
  const isMobile = width > 0 && width < 768;

  return (
    <div ref={containerRef} className="-m-4 sm:-m-6 h-[calc(100vh-3.5rem)] overflow-hidden bg-term-bg">
      {width > 0 && isMobile && (
        <div className="flex h-full flex-col gap-2 overflow-y-auto p-2">
          {(["chart", "ticket", "positions", "openorders", "watchlist", "book", "margin", "equity", "tape"] as const).map((key) => (
            <div key={key} className="term-card overflow-hidden" style={{ height: key === "chart" ? 320 : 240 }}>
              <div className="pane-header">
                <span>{paneTitle(key)}</span>
              </div>
              <div className="pane-body p-0" style={{ height: "calc(100% - 28px)" }}>
                {panels[key]}
              </div>
            </div>
          ))}
        </div>
      )}
      {width > 0 && !isMobile && (
        <GridLayout
          className="layout"
          layout={layout}
          cols={12}
          rowHeight={30}
          width={width}
          margin={[6, 6]}
          containerPadding={[6, 6]}
          draggableHandle=".pane-header"
          onLayoutChange={persist}
          compactType="vertical"
        >
          {Object.entries(panels).map(([key, node]) => (
            <div key={key} className="term-card overflow-hidden">
              <div className="pane-header">
                <span>{paneTitle(key)}</span>
                <span className="text-2xs text-term-mute">drag</span>
              </div>
              <div className="pane-body p-0">{node}</div>
            </div>
          ))}
        </GridLayout>
      )}
    </div>
  );
}

function paneTitle(key: string): string {
  switch (key) {
    case "chart": return "Chart";
    case "watchlist": return "Watchlist";
    case "book": return "Book";
    case "positions": return "Positions";
    case "openorders": return "Open Orders";
    case "margin": return "Margin";
    case "tape": return "Tape";
    case "ticket": return "Order Ticket";
    case "equity": return "Equity Curve";
    default: return key;
  }
}
